<!--
  Job setup — one per project.

  Three parts:
    0. The project's job defaults — product, SCI unit no, acceptance point.
       Spec, Data model / Project: "job defaults that never change". This is
       the ONLY screen that writes them, and the API seeds each new pour's
       header, material and calibration from them, so a crew member confirms
       rather than retypes them every pour.
    1. The stable part of the Material / Water / Pump sheet (water source,
       potable water documentation, pump line setup, acceptance point,
       manufacturer confirmation).
    2. ITP items 1-5, every one of which is a Hold point.

  Editing model, shared with PourRecord:
    - typing calls draft.setField(), which mirrors the change to localStorage
      within 200 ms, so a locked screen or a flat battery loses nothing;
    - `change` (i.e. blur with a modified value) flushes the accumulated delta
      to the API, and an explicit Save button does the same on demand;
    - a failed flush keeps the delta on the device and raises the unsaved bar
      in App.svelte with a retry. Nothing is dropped because a request failed.

  For a client session `canEdit` is false: inputs are replaced with plain text
  and no mutating call is issued at all.
-->
<script lang="ts">
  import { api, isApiError } from '../lib/api';
  import { linkHandler, routePath } from '../lib/router';
  import {
    canEdit,
    isHoldPoint,
    notifyError,
    project,
    sectionDraft,
    toText,
    type SectionDraft
  } from '../lib/store';
  import type { ItpStatus, JobSetup, JobSetupItem } from '../lib/types';

  /** Type alias (not interface) so it satisfies Record<string, unknown>. */
  type SetupFields = {
    water_source: string | null;
    potable_water_doc: string | null;
    pump_line_setup: string | null;
    acceptance_point: string | null;
    manufacturer_confirmation: string | null;
  };

  type NoteFields = Record<string, string | null>;

  /** Spec, Project: "job defaults that never change". */
  type DefaultFields = {
    product: string | null;
    sci_unit_no: string | null;
    acceptance_point: string | null;
  };

  const EMPTY_SETUP: SetupFields = {
    water_source: null,
    potable_water_doc: null,
    pump_line_setup: null,
    acceptance_point: null,
    manufacturer_confirmation: null
  };

  const DEFAULT_FIELDS: { field: keyof DefaultFields; label: string; hint: string }[] = [
    {
      field: 'product',
      label: 'Product',
      hint: 'Pre-fills "Product name" on every new pour.'
    },
    {
      field: 'sci_unit_no',
      label: 'SCI unit no',
      hint: 'Pre-fills the pour header, so it is confirmed rather than retyped.'
    },
    {
      field: 'acceptance_point',
      label: 'Acceptance point',
      hint: 'Pre-fills the pre-pour calibration acceptance point.'
    }
  ];

  let loading = $state(true);
  let loadError = $state<string | null>(null);
  let items = $state<JobSetupItem[]>([]);
  let setup = $state<JobSetup | null>(null);

  let values = $state<SetupFields>({ ...EMPTY_SETUP });
  let setupDirty = $state(false);
  let notes = $state<NoteFields>({});
  let notesDirty = $state(false);
  let savingStatusFor = $state<number | null>(null);
  let restoredDraft = $state(false);

  let setupDraft: SectionDraft<SetupFields> | undefined;
  let notesDraft: SectionDraft<NoteFields> | undefined;

  let defaults = $state<DefaultFields>({ product: null, sci_unit_no: null, acceptance_point: null });
  let defaultsDirty = $state(false);
  let defaultsDraft: SectionDraft<DefaultFields> | undefined;
  /** Plain (non-reactive) guard so the builder effect cannot loop on itself. */
  let defaultsBuiltFor = -1;

  function pickDefaults(row: {
    product: string | null;
    sci_unit_no: string | null;
    acceptance_point: string | null;
  }): DefaultFields {
    return {
      product: row.product,
      sci_unit_no: row.sci_unit_no,
      acceptance_point: row.acceptance_point
    };
  }

  /**
   * The job defaults live on the project row, not on job_setup, so they are
   * drafted separately and saved with PATCH /api/project.
   */
  $effect(() => {
    const current = $project;
    if (!current || defaultsBuiltFor === current.id) return;
    defaultsBuiltFor = current.id;
    defaultsDraft = sectionDraft<DefaultFields>({
      key: `project:${current.id}:defaults`,
      label: 'Job defaults',
      base: pickDefaults(current),
      save: async (changed) => {
        const response = await api.updateProject(changed);
        project.set(response.project);
        defaultsDraft?.rebase(pickDefaults(response.project));
      }
    });
    defaultsDraft.subscribe((next) => (defaults = { ...next }));
    defaultsDraft.dirty.subscribe((next) => (defaultsDirty = next));
  });

  function editDefault(field: keyof DefaultFields, raw: string) {
    defaultsDraft?.setField(field, toText(raw));
  }

  async function flushDefaults() {
    await defaultsDraft?.flush();
  }

  function pickSetup(row: JobSetup): SetupFields {
    return {
      water_source: row.water_source,
      potable_water_doc: row.potable_water_doc,
      pump_line_setup: row.pump_line_setup,
      acceptance_point: row.acceptance_point,
      manufacturer_confirmation: row.manufacturer_confirmation
    };
  }

  function replaceItem(item: JobSetupItem) {
    items = items.map((row) => (row.id === item.id ? item : row));
  }

  function buildDrafts(projectId: number, row: JobSetup, rows: JobSetupItem[]) {
    setupDraft = sectionDraft<SetupFields>({
      key: `project:${projectId}:job-setup`,
      label: 'Job setup — material, water and pump',
      base: pickSetup(row),
      save: async (changed) => {
        const response = await api.updateJobSetup(changed);
        setup = response.job_setup;
        items = response.items;
        setupDraft?.rebase(pickSetup(response.job_setup));
      }
    });
    setupDraft.subscribe((next) => (values = { ...next }));
    setupDraft.dirty.subscribe((next) => (setupDirty = next));

    notesDraft = sectionDraft<NoteFields>({
      key: `project:${projectId}:job-setup:notes`,
      label: 'Job setup item notes',
      base: Object.fromEntries(rows.map((item) => [String(item.id), item.notes])),
      save: async (changed) => {
        for (const [id, value] of Object.entries(changed)) {
          const response = await api.updateJobSetupItem(Number(id), { notes: value });
          replaceItem(response.item);
        }
        notesDraft?.rebase(
          Object.fromEntries(items.map((item) => [String(item.id), item.notes]))
        );
      }
    });
    notesDraft.subscribe((next) => (notes = { ...next }));
    notesDraft.dirty.subscribe((next) => (notesDirty = next));

    restoredDraft = setupDraft.restored || notesDraft.restored;
  }

  $effect(() => {
    void load();
  });

  async function load() {
    loading = true;
    loadError = null;
    try {
      let response = await api.getJobSetup();
      // GET /api/job-setup is strictly read-only: a project whose job_setup row
      // has never been created answers with an empty stand-in (id 0) rather
      // than inserting one, because that route is reachable by a read-only
      // client session. Crew materialise it here with an empty PATCH, which is
      // a crew-only route, so the ITP items get real ids to record against.
      if (response.job_setup.id <= 0 && $canEdit) {
        response = await api.updateJobSetup({});
      }
      setup = response.job_setup;
      items = response.items;
      buildDrafts(response.job_setup.project_id, response.job_setup, response.items);
    } catch (cause) {
      loadError = isApiError(cause) ? cause.message : 'Could not load the job setup.';
    } finally {
      loading = false;
    }
  }

  function editSetup(field: keyof SetupFields, raw: string) {
    setupDraft?.setField(field, toText(raw));
  }

  function editNote(itemId: number, raw: string) {
    notesDraft?.setField(String(itemId), toText(raw));
  }

  async function flushSetup() {
    await setupDraft?.flush();
  }

  async function flushNotes() {
    await notesDraft?.flush();
  }

  /**
   * Status is a single tap, so it saves immediately rather than waiting for a
   * blur. Tapping the current status again clears it back to pending, which is
   * how a mis-tap is corrected.
   */
  async function setStatus(item: JobSetupItem, status: ItpStatus) {
    if (!$canEdit) return;
    const next: ItpStatus = item.status === status ? 'pending' : status;
    savingStatusFor = item.id;
    replaceItem({ ...item, status: next }); // optimistic
    try {
      const response = await api.updateJobSetupItem(item.id, { status: next });
      replaceItem(response.item);
    } catch (cause) {
      replaceItem(item); // put the old value back; nothing was recorded
      notifyError(cause, `Could not record item ${item.item_no}`);
    } finally {
      savingStatusFor = null;
    }
  }

  const STATUS_CHOICES: { value: ItpStatus; label: string; cls: string }[] = [
    { value: 'done', label: 'Done', cls: 'is-ok' },
    { value: 'na', label: 'N/A', cls: '' },
    { value: 'failed', label: 'Failed', cls: 'is-fail' }
  ];

  const SETUP_FIELDS: { field: keyof SetupFields; label: string; hint?: string }[] = [
    { field: 'water_source', label: 'Water source' },
    { field: 'potable_water_doc', label: 'Potable water documentation' },
    { field: 'pump_line_setup', label: 'Pump / line setup' },
    { field: 'acceptance_point', label: 'Acceptance point' },
    { field: 'manufacturer_confirmation', label: 'Manufacturer pump/application confirmation' }
  ];
</script>

<div class="row row--between titlerow">
  <h1>Job setup</h1>
  <a class="btn btn--sm" href={routePath.home()} onclick={linkHandler}>Pours</a>
</div>
<p class="meta">{$project?.name ?? ''} — recorded once for the whole job.</p>

{#if restoredDraft}
  <div class="banner banner--warn" role="status">
    <div>
      <strong>Unsent changes restored</strong>
      Edits made on this device had not reached the server. They are shown below — check them,
      then save.
    </div>
  </div>
{/if}

{#if loadError}
  <div class="banner banner--error" role="alert">
    <div>
      <strong>Could not load</strong>{loadError}
      <div class="banner__actions">
        <button class="btn btn--sm" type="button" onclick={load}>Retry</button>
      </div>
    </div>
  </div>
{:else if loading}
  <p class="meta"><span class="spinner" aria-hidden="true"></span> Loading…</p>
{:else}
  {#if setup && setup.id <= 0 && $canEdit}
    <div class="banner banner--warn" role="status">
      <div>
        <strong>Job setup not created yet</strong>
        The record for this job has not been written to the server. Reload to create it before
        recording items 1–5.
        <div class="banner__actions">
          <button class="btn btn--sm" type="button" onclick={load}>Create it</button>
        </div>
      </div>
    </div>
  {/if}

  {#if $project}
    <div class="card">
      <div class="card__body">
        <h2>Job defaults</h2>
        <p class="meta">
          Set once for {$project.name}. Every new pour starts from these rather than a blank line.
        </p>

        {#each DEFAULT_FIELDS as entry (entry.field)}
          <div class="field">
            <span class="label" id={`label-default-${entry.field}`}>{entry.label}</span>
            {#if $canEdit}
              <input
                type="text"
                value={defaults[entry.field] ?? ''}
                aria-labelledby={`label-default-${entry.field}`}
                aria-describedby={`hint-default-${entry.field}`}
                autocapitalize="sentences"
                oninput={(event) => editDefault(entry.field, event.currentTarget.value)}
                onchange={flushDefaults}
              />
            {:else}
              <div class="readonly-value" class:is-empty={!defaults[entry.field]}>
                {defaults[entry.field] ?? ''}
              </div>
            {/if}
            <span class="hint" id={`hint-default-${entry.field}`}>{entry.hint}</span>
          </div>
        {/each}

        {#if $canEdit}
          <button
            class="btn btn--primary btn--block"
            type="button"
            disabled={!defaultsDirty}
            onclick={flushDefaults}
          >
            {defaultsDirty ? 'Save job defaults' : 'Saved'}
          </button>
        {/if}
      </div>
    </div>
  {/if}

  <div class="card">
    <div class="card__body">
      <h2>Material, water and pump</h2>
      <p class="meta">The part of the sheet that does not change pour to pour.</p>

      {#each SETUP_FIELDS as entry (entry.field)}
        <div class="field">
          <span class="label" id={`label-${entry.field}`}>{entry.label}</span>
          {#if $canEdit}
            <input
              type="text"
              value={values[entry.field] ?? ''}
              aria-labelledby={`label-${entry.field}`}
              autocapitalize="sentences"
              oninput={(event) => editSetup(entry.field, event.currentTarget.value)}
              onchange={flushSetup}
            />
          {:else}
            <div class="readonly-value" class:is-empty={!values[entry.field]}>
              {values[entry.field] ?? ''}
            </div>
          {/if}
        </div>
      {/each}

      {#if $canEdit}
        <button
          class="btn btn--primary btn--block"
          type="button"
          disabled={!setupDirty}
          onclick={flushSetup}
        >
          {setupDirty ? 'Save changes' : 'Saved'}
        </button>
      {/if}
    </div>
  </div>

  <div class="card">
    <div class="card__body">
      <h2>ITP items 1–5</h2>
      <p class="meta">Pre-job checks. Every one of these is a Hold point.</p>

      {#each items as item (item.id)}
        {@const hold = isHoldPoint(item.point_type)}
        <div class="item" class:item--hold={hold} class:item--failed={item.status === 'failed'}>
          <div class="item__head">
            <span class="item__no">{item.item_no}</span>
            <span class="item__title">{item.title}</span>
            {#if hold}<span class="holdmark">HOLD</span>{/if}
            <span class="pill pill--pending">{item.point_type}</span>
          </div>

          {#if $canEdit && item.id > 0}
            <div class="segmented" role="group" aria-label={`Status for item ${item.item_no}`}>
              {#each STATUS_CHOICES as choice (choice.value)}
                <button
                  type="button"
                  class={choice.cls}
                  aria-pressed={item.status === choice.value}
                  disabled={savingStatusFor === item.id}
                  onclick={() => setStatus(item, choice.value)}
                >
                  {choice.label}
                </button>
              {/each}
            </div>
          {:else}
            <div class="row">
              <span
                class="pill {item.status === 'done'
                  ? 'pill--ok'
                  : item.status === 'failed'
                    ? 'pill--hold'
                    : 'pill--pending'}"
              >
                {item.status === 'na' ? 'N/A' : item.status}
              </span>
            </div>
          {/if}

          <div class="stampline meta">
            {#if item.initials}{item.initials}{/if}
            {#if item.recorded_at}
              · {new Date(item.recorded_at).toLocaleString()}
            {/if}
          </div>

          {#if $canEdit && item.id > 0}
            <textarea
              placeholder="Notes"
              aria-label={`Notes for item ${item.item_no}`}
              value={notes[String(item.id)] ?? ''}
              oninput={(event) => editNote(item.id, event.currentTarget.value)}
              onchange={flushNotes}
            ></textarea>
          {:else if item.notes}
            <p class="notes">{item.notes}</p>
          {/if}
        </div>
      {/each}

      {#if $canEdit && notesDirty}
        <button class="btn btn--primary btn--block" type="button" onclick={flushNotes}>
          Save notes
        </button>
      {/if}
    </div>
  </div>
{/if}

<style>
  .titlerow {
    margin-top: 0.25rem;
  }

  .stampline {
    min-height: 1rem;
    margin: 0.35rem 0;
  }

  .notes {
    margin: 0.35rem 0 0;
    white-space: pre-wrap;
  }

  .hint {
    font-size: 0.75rem;
    line-height: 1.25;
    color: var(--fg-muted, #3f4750);
  }
</style>
